import type { FormEvent, ReactNode } from "react"
import { Head, useForm, usePage } from "@inertiajs/react"
import { BackfillCard } from "@/components/settings/BackfillCard"
import { ReadOnlySettings, type AgencySettings } from "@/components/settings/ReadOnlySettings"
import { SettingsLayout } from "@/components/settings/SettingsLayout"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import type { PageProps } from "@/types/inertia"

type SettingsPageProps = {
  readonly agency: AgencySettings
  readonly backfill: {
    readonly last_run: Record<string, unknown> | null
  }
}

export default function Settings({ agency, backfill }: SettingsPageProps) {
  const { props } = usePage<PageProps<SettingsPageProps>>()
  const currentUser = props.current_user
  const errors = props.errors ?? {}
  const description =
    "Manage agency models, AI budget, SmartLead credentials, and the live-action arm switch."
  const form = useForm({
    classifier_model: agency.classifier_model,
    draft_model: agency.draft_model,
    daily_ai_budget_usd: String(agency.daily_ai_budget_usd),
    actions_armed: agency.actions_armed,
    smartlead_api_key: "",
    openrouter_api_key: "",
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    form.patch("/settings", {
      preserveScroll: true,
      onSuccess: () => form.reset("smartlead_api_key", "openrouter_api_key"),
    })
  }

  return (
    <>
      <Head title="Settings">
        <meta name="description" content={description} />
        <meta property="og:title" content="Settings" />
        <meta property="og:description" content={description} />
      </Head>
      <SettingsLayout>
        <h1>Settings</h1>
        <p className="mt-2">Agency controls for {agency.name}.</p>

        {props.flash?.notice && (
          <div className="mt-6 rounded-md border border-accent bg-accent-faded px-4 py-3 text-sm text-accent-display">
            {props.flash.notice}
          </div>
        )}

        {currentUser?.admin ? (
          <>
          <form onSubmit={submit} className="mt-8 space-y-8">
            <section className="rounded-lg border border-hairline bg-page p-6">
              <div className="grid gap-5 sm:grid-cols-2">
                <FieldError error={errors.classifier_model}>
                  <label htmlFor="classifier_model">Classifier model</label>
                  <Input
                    id="classifier_model"
                    value={form.data.classifier_model}
                    aria-invalid={!!errors.classifier_model}
                    onChange={(event) =>
                      form.setData("classifier_model", event.target.value)
                    }
                  />
                </FieldError>

                <FieldError error={errors.draft_model}>
                  <label htmlFor="draft_model">Draft model</label>
                  <Input
                    id="draft_model"
                    value={form.data.draft_model}
                    aria-invalid={!!errors.draft_model}
                    onChange={(event) =>
                      form.setData("draft_model", event.target.value)
                    }
                  />
                </FieldError>

                <FieldError error={errors.daily_ai_budget_usd}>
                  <label htmlFor="daily_ai_budget_usd">Daily AI budget USD</label>
                  <Input
                    id="daily_ai_budget_usd"
                    type="number"
                    min="0"
                    step="1"
                    value={form.data.daily_ai_budget_usd}
                    aria-invalid={!!errors.daily_ai_budget_usd}
                    onChange={(event) =>
                      form.setData("daily_ai_budget_usd", event.target.value)
                    }
                  />
                </FieldError>
              </div>
            </section>

            <section className="rounded-lg border border-hairline bg-page p-6">
              <h2>API keys</h2>
              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <FieldError error={errors.smartlead_api_key}>
                  <label htmlFor="smartlead_api_key">SmartLead API key</label>
                  <Input
                    id="smartlead_api_key"
                    type="password"
                    autoComplete="off"
                    placeholder={
                      agency.smartlead_api_key_set ? "•••••••• (set)" : "not set"
                    }
                    value={form.data.smartlead_api_key}
                    aria-invalid={!!errors.smartlead_api_key}
                    onChange={(event) =>
                      form.setData("smartlead_api_key", event.target.value)
                    }
                  />
                  <div className="text-sm text-ink-muted">
                    Leave blank to keep the current key
                  </div>
                </FieldError>

                <FieldError error={errors.openrouter_api_key}>
                  <label htmlFor="openrouter_api_key">OpenRouter API key</label>
                  <Input
                    id="openrouter_api_key"
                    type="password"
                    autoComplete="off"
                    placeholder={
                      agency.openrouter_api_key_set ? "•••••••• (set)" : "not set"
                    }
                    value={form.data.openrouter_api_key}
                    aria-invalid={!!errors.openrouter_api_key}
                    onChange={(event) =>
                      form.setData("openrouter_api_key", event.target.value)
                    }
                  />
                  <div className="text-sm text-ink-muted">
                    Leave blank to keep the current key
                  </div>
                </FieldError>
              </div>
            </section>

            <section className="rounded-lg border border-danger bg-danger-faded p-6">
              <label
                htmlFor="actions_armed"
                className="flex items-start gap-3 font-normal text-ink-body"
              >
                <Checkbox
                  id="actions_armed"
                  checked={form.data.actions_armed}
                  onChange={(event) =>
                    form.setData("actions_armed", event.target.checked)
                  }
                />
                <span>
                  <span className="block font-medium text-danger-display">
                    Arm live SmartLead actions
                  </span>
                  <span className="mt-1 block text-sm text-ink-body">
                    Armed means live SmartLead mutations. Disarmed means dry-run
                    audit rows only.
                  </span>
                </span>
              </label>
            </section>

            <div className="flex justify-end">
              <Button type="submit" disabled={form.processing}>
                Save settings
              </Button>
            </div>
          </form>

          <BackfillCard lastRun={backfill.last_run} />
          </>
        ) : (
          <ReadOnlySettings agency={agency} />
        )}
      </SettingsLayout>
    </>
  )
}

function FieldError({
  children,
  error,
}: {
  readonly children: ReactNode
  readonly error?: string
}) {
  return (
    <div className="space-y-2">
      {children}
      {error && <div className="text-sm text-danger-display">{error}</div>}
    </div>
  )
}
